import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CPMemberService } from 'src/app/cp-member/services/CPMember.service';
import { AlertService } from 'src/app/shared/services/alert.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { EncruptioDecryptionService } from 'src/app/shared/services/encryption-decryption.service';
import * as CryptoJS from 'crypto-js';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit {
  saveButtonText = 'Submit';
  title = 'Set New Password';
  disableSaveButton = true;
  form: FormGroup;
  oldPassError = '';
  newPassError = '';
  confirmPassError = '';
  isPasswordChangedSuccessfully = false;
  private subs = new Subscription();
  error = null;
  constructor(
    private authService: AuthService,
    private alertService: AlertService,
    private cpMemberService: CPMemberService,
    private fb: FormBuilder,
    private encryptDecrypt : EncruptioDecryptionService
  ) {
    this.form = this.fb.group(
      {
        oldPass: ['', [Validators.required]],
        newPass: [
          '',
          [
            Validators.required,
            Validators.minLength(14),
            Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{14,}$/),
          ],
        ],
        confirmPass: ['', [Validators.required]],
      },
      {
        validators: [this.confirmMatchValidator()]
      }
    );

    // this.subs.add(
    //   this.form.valueChanges.subscribe(() => this.updateErrorMessages())
    // );

    // this.subs.add(
    //   this.form.statusChanges.subscribe(() => this.updateErrorMessages())
    // );

    // this.updateErrorMessages();
  }

  ngOnInit(): void {}

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  passwordRulesValidator(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';
    const errors: any = {};

    if (value.length < 14) {
      errors.minlength = { requiredLength: 14, actualLength: value.length };
    }
    if (!/[A-Z]/.test(value)) {
      errors.uppercase = true;
    }
    if (!/[a-z]]/.test(value)) {
      errors.lowercase = true;
    }
    if (!/[0-9]/.test(value)) {
      errors.number = true;
    }
    if (!/[^A-Za-z0-9]/.test(value)) {
      errors.specialChar = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  private confirmMatchValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const newPass = group.get('newPass');
      const confirmPass = group.get('confirmPass');

      if (!newPass || !confirmPass) return null;

      const np = newPass.value;
      const cp = confirmPass.value;
      const passwordMismatch = !!np && !!cp && np !== cp;

      const currentErrors = confirmPass.errors || {};

      if (passwordMismatch) {
        if (!currentErrors['passwordMismatch']) {
          confirmPass.setErrors({ ...currentErrors, passwordMismatch: true });
        }
      } else {
        if (confirmPass.hasError('passwordMismatch')) {
          const { passwordMismatch, ...rest } = currentErrors;
          confirmPass.setErrors(Object.keys(rest).length ? rest : null);
        }
      }

      return null; 
    };
  }

  private updateErrorMessages() {
    const oldPass = this.form.get('oldPass');
    this.oldPassError = '';
    if (oldPass?.touched || oldPass?.dirty || this.form?.touched) {
      this.oldPassError = 'Old Password is required.';
    }

    const newPass = this.form.get('newPass');
    this.newPassError = '';
    if (newPass) {
      const errs = newPass.errors || {};
      if (newPass?.touched || newPass?.dirty || this.form?.touched) {
        if (errs.required) {
          this.newPassError = 'New Password is required.';
        } else if (errs.minLength) {
          const actual = errs.minLength.actualLength || 0;
          this.newPassError = `Minimum 14 characters required (${actual}/14.`;
        } else if (errs.uppercase) {
          this.newPassError = 'Atleast one uppercase letter is required.';
        } else if (errs.lowercase) {
          this.newPassError = 'Atleast one lowercase letter is required.';
        } else if (errs.number) {
          this.newPassError = 'Atleast one number is required.';
        } else if (errs.specialChar) {
          this.newPassError = 'Atleast one special character is required (e.g., @,#,$ etc).';
        }
      }
    }

    const confirmPass = this.form.get('confirmPass');
    this.confirmPassError = '';
    if (confirmPass) {
      if (confirmPass.touched || confirmPass.dirty || this.form.touched) {
        if (confirmPass.hasError('required')) {
          this.confirmPassError = 'Please confirm the new password.';
        } else if (this.form.hasError('passwordMismatch')) {
          this.confirmPassError = 'Passwords do not match.';
        }
      } else {
        if (this.form.hasError('passwordMismatch') && confirmPass.value) {
          this.confirmPassError = 'Passwords do not match.';
        }
      }
    }
  }

  close() {
    this.isPasswordChangedSuccessfully ? this.signout() : '';
    if (!this.isPasswordChangedSuccessfully) {
    this.form.markAllAsTouched();
    this.updateErrorMessages();
    if (this.form.invalid) {
      return;
    } else {
      console.log(this.authService.getUserId());
    }
    const formValue = this.form.value;

    const jsonPayload = JSON.stringify(formValue);
    const key= CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex);
    const iv= CryptoJS.lib.WordArray.random(8).toString(CryptoJS.enc.Hex);
  
    const userNameSub = this.authService.userName.subscribe({
      next: name => {
        this.cpMemberService
          .changePassword({
            OldPassword: this.encryptDecrypt.encryptChangePass(
              this.form.get('oldPass').value,
              key,
              iv
            ),
            NewPassword:this.encryptDecrypt.encryptChangePass(
              this.form.get('newPass').value,
              key,
              iv
            ),
            UserName: name,
            Key: key,
            IV: iv,
          })
          .then(res => {
            this.isPasswordChangedSuccessfully = true;
            this.saveButtonText = 'OK';
          })
          .catch(err => {
            this.alertService.error(err.error);
            this.error = err;
          });
        // this.modalService.dismissAll(EnforcePasswordChangeComponent);
        // this.signout();
      },
      error: () => {
        userNameSub.unsubscribe();
      },
    });
  }
  }

  checkBoxClicked() {
    const checkbox = document.querySelector('#consent1') as HTMLInputElement;
    if (checkbox.checked) {
      this.disableSaveButton = false;
    } else {
      this.disableSaveButton = true;
    }
  }
  signout() {
    this.authService.signout();
  }
}

